// components/LSSF/ContactEnquiry.jsx
import React, { useState, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import { MdDelete, MdSearch, MdVisibility, MdMarkEmailRead, MdRefresh } from 'react-icons/md';

const ContactEnquiry = () => {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const limit = 20;

  const getContacts = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${base_url}/api/v1/mbbs/contact`);
      setData(res.data.data);
      setFilteredData(res.data.data);
      const total = Math.ceil(res.data.data.length / limit);
      setTotalPages(total > 0 ? total : 1);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      toast.error('Failed to fetch contacts');
      setLoading(false);
    }
  };

  useEffect(() => {
    getContacts();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredData(data);
    } else {
      const searchLower = searchTerm.toLowerCase();
      const filtered = data.filter(item => {
        return (
          (item.name && item.name.toLowerCase().includes(searchLower)) ||
          (item.email && item.email.toLowerCase().includes(searchLower)) ||
          (item.mobile && item.mobile.includes(searchLower)) ||
          (item.description && item.description.toLowerCase().includes(searchLower))
        );
      });
      setFilteredData(filtered);
    }
  }, [searchTerm, data]);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${base_url}/api/v1/contacts/${id}/read`);
      toast.success('Marked as read');
      getContacts();
    } catch (err) {
      console.error('Error:', err);
      toast.error('Failed to update status');
    }
  };

  const deleteContact = async (id) => {
    const choice = window.confirm('Are you sure you want to delete this enquiry?');
    if (choice) {
      try {
        await axios.delete(`${base_url}/api/v1/contacts/${id}`);
        toast.success('Enquiry deleted successfully');
        getContacts();
      } catch (err) {
        console.error('Delete error:', err);
        toast.error('Failed to delete enquiry');
      }
    }
  };

  const viewDetails = (contact) => {
    setSelectedContact(contact);
    setShowModal(true);

  };

  const paginatedData = filteredData.slice((page - 1) * limit, page * limit);

  return (
    <div className="page-wrapper">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header row d-flex justify-content-between align-items-center">
                <div className="col-md-4">
                  <h4 className="mb-0">Contact Enquiries</h4>
                </div>
                <div className="col-md-4">
                  <div className="input-group">
                    <span className="input-group-text">
                      <MdSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by name, email, mobile..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-4 text-end">
                  <button className="btn btn-success btn-sm me-2" onClick={getContacts}>
                    <MdRefresh /> Refresh
                  </button>
                  <span className="badge bg-warning p-2">
                    Unread: {data.filter(item => !item.is_read).length}
                  </span>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-bordered shadow-sm mt-3" cellPadding={5}>
                    <thead className="bg-light">
                      <tr>
                        <th>S.No.</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>Message</th>
                        <th>Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          {[...Array(8)].map((_, i) => (
                            <td key={i}><Skeleton count={1} /></td>
                          ))}
                        </tr>
                      ) : paginatedData.length > 0 ? (
                        paginatedData.map((item, index) => (
                          <tr key={item.id} className={!item.is_read ? 'table-warning' : ''}>
                            <td>{(page - 1) * limit + index + 1}</td>
                            <td>
                              <strong>{item.name}</strong>
                              {!item.is_read && <span className="badge bg-danger ms-2">New</span>}
                            </td>
                            <td>{item.email}</td>
                            <td>{item.mobile || '-'}</td>
                            <td>
                              {item.description?.length > 50 
                                ? `${item.description.substring(0, 50)}...` 
                                : item.description || '-'}
                            </td>
                            <td>{new Date(item.created_at).toLocaleDateString()}</td>
                            <td>
                              <button 
                                className="btn btn-sm btn-info me-1"
                                onClick={() => viewDetails(item)}
                              >
                                <MdVisibility size={18} />
                              </button>

                              <button 
                                className="btn btn-sm btn-danger"
                                onClick={() => deleteContact(item.id)}
                              >
                                <MdDelete size={18} />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className="text-center text-danger">
                          <td colSpan={8}>No enquiries found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {filteredData.length > limit && (
                  <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      Previous
                    </button>
                    <span>Page {page} of {totalPages}</span>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={page === totalPages}
                      onClick={() => setPage(page + 1)}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Details Modal */}
      {showModal && selectedContact && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title">Contact Enquiry Details</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <strong>Name:</strong>
                    <p>{selectedContact.name}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Email:</strong>
                    <p>{selectedContact.email}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Mobile:</strong>
                    <p>{selectedContact.mobile || 'Not provided'}</p>
                  </div>
                  
                  <div className="col-md-12 mb-3">
                    <strong>Message:</strong>
                    <p className="mt-2 p-3 bg-light rounded">{selectedContact.description || 'No message'}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Submitted Date:</strong>
                    <p>{new Date(selectedContact.created_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Close
                </button>
                <button type="button" className="btn btn-primary" onClick={() => window.print()}>
                  Print
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default ContactEnquiry;